import React, { useEffect } from 'react';

import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

import * as S from './styles';
import { theme } from '../../styles/theme';
import { Auxiliar, BodyMSpan, TaglineP, TaglinePSpan } from '../../styles/stylesTypography';

const CardRTwithGraphic = ({ ...props }) => {
    // Recebe os dados dos agrônomos via props ou usa array vazio como fallback
    const agronomos = props.agronomos || [];

    // Função para adaptar os dados dos agrônomos para o formato esperado pelos cards
    const formatOneDecimal = (v) => {
        if (typeof v !== 'number' || isNaN(v)) return '0';
        const floored = Math.floor(v * 10) / 10;
        if (floored < 0.1) return '0';
        return floored.toFixed(1).replace('.', ',');
    };

    const toISODateLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const toDateKey = (input) => {
        if (!input) return null;
        if (typeof input === 'string') {
            if (input.length >= 10) return input.slice(0, 10);
            const parsed = new Date(input);
            return isNaN(parsed.getTime()) ? null : toISODateLocal(parsed);
        }
        if (input instanceof Date) return isNaN(input.getTime()) ? null : toISODateLocal(input);
        const parsed = new Date(input);
        return isNaN(parsed.getTime()) ? null : toISODateLocal(parsed);
    };

    const buildLastNDaysSeries = (source, daysCount = 11) => {
        const map = new Map();
        if (Array.isArray(source)) {
            for (const r of source) {
                const key = toDateKey(r?.data);
                if (!key) continue;
                const totalRaw = Number(r?.total);
                const total = Number.isFinite(totalRaw) ? totalRaw : 0;
                map.set(key, total);
            }
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const result = [];
        for (let i = daysCount - 1; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const key = toISODateLocal(d);
            result.push({
                id: result.length + 1,
                day: key,
                revenues: map.get(key) ?? 0
            });
        }
        return result;
    };

    const formatAgronomoData = (agronomo, crea, creaIndex) => {
        const agronomistId =
            agronomo?.agronomist_id ??
            agronomo?.agro_agronomist_id ??
            crea?.agro_agronomist_id ??
            agronomo?.id ??
            0;

        const creaId = crea?.id ?? crea?.crea ?? creaIndex ?? 0;
        const cardId = `${agronomistId}_${creaId}`;

        const nomeBase =
            agronomo?.agronomist_nome ??
            agronomo?.nome ??
            (agronomistId ? `Agrônomo ${agronomistId}` : 'N/A');

        const tipoBase = (crea?.tipo || 'crea').toString().toUpperCase();
        const creaNumberBase = crea?.crea ?? agronomo?.crea_numero ?? 'N/A';
        const estadoBase = crea?.estado ?? agronomo?.estado ?? 'N/A';
        const creaLabel = `${tipoBase}: ${creaNumberBase}${estadoBase ? ` - ${estadoBase}` : ''}`;
        const artsBase =
            typeof crea?.arts_disponiveis === 'number'
                ? crea.arts_disponiveis
                : typeof agronomo?.arts_disponiveis === 'number'
                    ? agronomo.arts_disponiveis
                    : 0;

        const receitasDiaBase = crea?.media_receitas_mes ?? agronomo?.media_receitas_mes ?? 0;

        const chartSource = Array.isArray(crea?.receitas_por_dia_ultimos11dias)
            ? crea.receitas_por_dia_ultimos11dias
            : Array.isArray(crea?.receitas_por_dia_ultimo_mes)
                ? crea.receitas_por_dia_ultimo_mes
                : [];

        const revenues_data = buildLastNDaysSeries(chartSource, 11);

        return {
            id: agronomistId,
            card_id: cardId,
            nome: nomeBase,
            crea_number: creaNumberBase,
            estado: estadoBase,
            crea_label: creaLabel,
            receitas: formatOneDecimal(receitasDiaBase),
            arts: artsBase,
            receitas_disponiveis: typeof crea?.receitas_disponiveis === 'number' ? crea.receitas_disponiveis : (typeof agronomo?.receitas_disponiveis === 'number' ? agronomo.receitas_disponiveis : 0),
            contratos_disponiveis: typeof crea?.contratos_disponiveis === 'number' ? crea.contratos_disponiveis : (typeof agronomo?.contratos_disponiveis === 'number' ? agronomo.contratos_disponiveis : 0),
            revenues_data
        };
    };

    // Converte os dados dos agrônomos para o formato dos cards
    const formattedAgronomos = (Array.isArray(agronomos) ? agronomos : []).reduce((acc, agronomo) => {
        const creas = Array.isArray(agronomo?.agro_creas) ? agronomo.agro_creas : null;

        if (Array.isArray(creas) && creas.length > 0) {
            for (let i = 0; i < creas.length; i++) {
                acc.push(formatAgronomoData(agronomo, creas[i], i));
            }
            return acc;
        }

        const looksLikeCrea =
            agronomo &&
            (Object.prototype.hasOwnProperty.call(agronomo, 'receitas_por_dia_ultimos11dias') ||
                Object.prototype.hasOwnProperty.call(agronomo, 'receitas_por_dia_ultimo_mes'));

        if (looksLikeCrea) {
            acc.push(formatAgronomoData(agronomo, agronomo, 0));
            return acc;
        }

        acc.push(formatAgronomoData(agronomo, null, 0));
        return acc;
    }, []);

    const formatChartData = (item) => {
        return item.revenues_data.map(data => {
            const rawDay = data.day;
            if (typeof rawDay === 'number') {
                return {
                    day: String(rawDay),
                    value: data.revenues
                };
            }

            const dayStr = (rawDay || '').toString();
            if (!dayStr.includes('-')) {
                return {
                    day: dayStr,
                    value: data.revenues
                };
            }

            const parts = dayStr.split('-');
            const dayNum = parseInt(parts[2], 10);
            const monthNum = parseInt(parts[1], 10);
            return {
                day: `${isNaN(dayNum) ? '' : dayNum}/${isNaN(monthNum) ? '' : monthNum}`,
                value: data.revenues
            };
        });
    };

    const total = formattedAgronomos.length;
    const startIndex = props.page && props.itemsPerPage ? (props.page - 1) * props.itemsPerPage : 0;
    const endIndex = props.itemsPerPage ? startIndex + props.itemsPerPage : 4;

    const visibleItems = props.showAll
        ? (props.serverSide ? formattedAgronomos.slice(0, props.itemsPerPage || 8) : formattedAgronomos.slice(startIndex, endIndex))
        : formattedAgronomos.slice(0, 4);

    useEffect(() => {
        if (typeof props.onCountChange === 'function') {
            props.onCountChange(total, visibleItems.length);
        }
    }, [props.page, props.itemsPerPage, props.showAll, agronomos]);

    return (
        <S.Container>
            <S.Wrapper>
                {visibleItems.map((item) => (
                    <S.Card 
                    key={item.card_id} 
                    onClick={() => window.location.href = `/agronomos/${item.id}`}
                    >
                        <S.CardHeader>
                            <Auxiliar>
                                {item.crea_label}
                            </Auxiliar>

                            <BodyMSpan>
                                {item.nome}
                            </BodyMSpan>
                        </S.CardHeader>

                        <S.CardGraphic>
                            <S.ChartContainer>
                                <ResponsiveContainer width="100%" height="100%">
                                    {(() => {
                                        const chartData = formatChartData(item);
                                        const maxVal = chartData.length > 0 ? Math.max(...chartData.map(d => d.value)) : 0;
                                        const maxTick = Math.max(10, Math.ceil(maxVal / 10) * 10);
                                        const yTicks = Array.from({ length: Math.floor(maxTick / 10) + 1 }, (_, i) => i * 10);
                                        return (
                                            <AreaChart
                                                data={chartData}
                                                margin={{ top: 5, right: 15, left: 0, bottom: 0 }}
                                            >
                                                <defs>
                                                    <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor={theme.colors.green.Verde_AgroReceita} stopOpacity={0.4} />
                                                        <stop offset="100%" stopColor={theme.colors.green.Verde_AgroReceita} stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>

                                                <XAxis
                                                    dataKey="day"
                                                    tick={{fontSize: 9, fill: "#21262F", angle: -35, textAnchor: 'end' }}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    tickMargin={10}
                                                    interval={0}
                                                    minTickGap={0}
                                                    height={35}
                                                />

                                                <YAxis
                                                    tick={{ fontSize: 9, fill: "#21262F" }}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    tickMargin={5}
                                                    width={30}
                                                    allowDecimals={false}
                                                    domain={[0, maxTick]}
                                                    ticks={yTicks}
                                                    interval={0}
                                                />

                                                <Tooltip 
                                                    formatter={(val) => [val, 'Receitas']}
                                                />

                                                <Area
                                                    type="linear"
                                                    dataKey="value"
                                                    stroke={theme.colors.green.Verde_AgroReceita}
                                                    strokeWidth={2}
                                                    fill="url(#colorGreen)"
                                                />
                                            </AreaChart>
                                        );
                                    })()}
                                </ResponsiveContainer>
                            </S.ChartContainer>
                        </S.CardGraphic>

                        <S.Divider />

                        <S.CardFooterInfo>
                            <TaglineP>
                                {item.receitas} receitas/dia
                            </TaglineP>

                            <TaglinePSpan color={
                                item.estado !== "SP" ? 
                                 (item.receitas_disponiveis <= 5 ? theme.colors.red.Tostado : item.receitas_disponiveis <= 10 ? theme.colors.orange.Laranja : theme.colors.gray.Cinza_2) : 
                                  (item.contratos_disponiveis <= 5 ? theme.colors.red.Tostado : item.contratos_disponiveis <= 10 ? theme.colors.orange.Laranja : theme.colors.gray.Cinza_2)}>
                                {item.estado !== "SP" ? ' Saldo receitas:' : 'Saldo contratos:'}
                                
                                {item.estado !== "SP" ? item.receitas_disponiveis : item.contratos_disponiveis} 
                            </TaglinePSpan>
                        </S.CardFooterInfo>
                    </S.Card>
                ))}
            </S.Wrapper>
        </S.Container>
    );
};

export default CardRTwithGraphic;